import { useEffect, useMemo, useState } from 'react'
import { User } from '../../types'
import { auditService } from '../../services/auditService'
import {
  listTeamMembers, saveTeamMember, setTeamMemberActive,
  listAttributions, listDealsBrief, saveAttribution, updateAttribution, deleteAttribution, AttributionRow, DealBrief,
  listCommissionInvoicesAdmin, saveCommissionInvoice, deleteCommissionInvoice, CommInvoiceRow,
  uploadCommissionInvoiceFile, commissionInvoiceFileUrl,
  listDealsForCosts, saveCapitalisedCosts, CostRow,
} from '../../services/commissionService'
import { TeamMember } from '../../types'
import { commissionTerms, termsLabel } from '../../lib/margin'
import FileDropZone from '../FileDropZone'
import { extractText } from '../../services/supplierInvoiceService'
import { parseInvoiceText } from '../../lib/invoiceParse'
import './CommissionAdminView.css'
import { toast } from '../Toast'

/* What the attribution form hands back: the member's terms as they stand right now,
   snapshotted onto the deal. Basis as well as number, since 13 Sep. */
interface AttributionInput {
  deal_id: string
  team_member_id: string
  role: string
  basis: string
  rate: number
  fixed_amount: number
}

type Tab = 'members' | 'attributions' | 'invoices' | 'costs'

/*
 * Winnie, 1 Sep: "All report: should have two decimals instead of rounding amount."
 * A commission is a percentage of a margin and almost never lands on a round
 * dollar, so a rounded figure here disagrees with what is actually paid.
 */
const money = (n: number | null | undefined, dp = 2) =>
  n == null ? '—' : `$${Number(n).toLocaleString('en-AU', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`
/*
 * A commission rate is not always a whole percent.
 *
 * Beta test, 6 Sep: this rounded on display AND on the editable input, so a
 * rate of 2.5% showed as 3% and, if anyone opened that row and saved it, was
 * written back to the database as 3%. Nobody has been bitten yet - every rate
 * on file today is 25% or 50% - but the field was quietly lossy.
 *
 * toFixed(2) then Number() drops trailing zeros, so 25% still reads "25%"
 * rather than "25.00%".
 */
const asPercent = (rate: number) => Number(((rate || 0) * 100).toFixed(2))
/* Winnie, 13 Sep: "Need the option to either enter fix rate or % rate for commission."
   One label for both, so a fixed-fee member never reads as "0%" on a screen that has
   always shown percentages. */
const terms = (m: { commission_basis?: string | null; commission_rate?: number | null; commission_amount?: number | null }) =>
  termsLabel(commissionTerms(m))
const roleName = (r: string) => (r === 'buyer_sourcer' ? 'Buyer-Sourcer' : r === 'sales' ? 'Sales' : 'Spotter')
const isoDate = (iso?: string | null) => (!iso ? '—' : new Date(iso).toISOString().slice(0, 10))
const todayISO = () => new Date().toISOString().slice(0, 10)

export default function CommissionAdminView({ user }: { user: User }) {
  const [tab, setTab] = useState<Tab>('members')
  const tabs: { id: Tab; label: string; icon: string }[] = [
    { id: 'members', label: 'Team Members', icon: '👥' },
    { id: 'attributions', label: 'Deal Attributions', icon: '🔗' },
    { id: 'invoices', label: 'Commission Invoices', icon: '🧾' },
    { id: 'costs', label: 'Capitalised Costs', icon: '🧮' },
  ]
  return (
    <div className="cadmin">
      <div className="ca-head">
        <h2>Commissions Settings</h2>
        <p className="ca-sub">Manage team members, credit deals, log received commission invoices, and set capitalised costs — the data behind the Sales Performance &amp; Commission Recon reports.</p>
      </div>
      <div className="ca-tabs">
        {tabs.map((t) => (
          <button key={t.id} className={`ca-tab ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>
            <span>{t.icon}</span> {t.label}
          </button>
        ))}
      </div>
      {tab === 'members' && <MembersTab user={user} />}
      {tab === 'attributions' && <AttributionsTab user={user} />}
      {tab === 'invoices' && <InvoicesTab user={user} />}
      {tab === 'costs' && <CostsTab user={user} />}
    </div>
  )
}

function Toast({ msg }: { msg: string | null }) {
  if (!msg) return null
  return <div className="ca-toast">{msg}</div>
}

// ==========================================================================
// TEAM MEMBERS
// ==========================================================================

function MembersTab({ user }: { user: User }) {
  const [rows, setRows] = useState<TeamMember[]>([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const [editing, setEditing] = useState<Partial<TeamMember> | null>(null)

  const load = () => {
    setLoading(true)
    listTeamMembers().then(setRows).catch((e) => setErr(e.message)).finally(() => setLoading(false))
  }
  useEffect(load, [])

  const save = async (m: Partial<TeamMember>) => {
    await saveTeamMember({
      id: m.id, name: m.name!, email: m.email, role: m.role || 'spotter',
      commission_basis: m.commission_basis || 'percent',
      commission_rate: m.commission_rate ?? 0.25,
      commission_amount: m.commission_amount ?? 0,
      risk_rating_days: m.risk_rating_days ?? 7, active: m.active ?? true,
    })
    await auditService.log(user, m.id ? 'team_member_updated' : 'team_member_created', { targetName: m.name, details: { role: m.role, basis: m.commission_basis || 'percent', rate: m.commission_rate, amount: m.commission_amount } })
    setEditing(null); setToast('Team member saved'); setTimeout(() => setToast(null), 2500); load()
  }
  const toggleActive = async (m: TeamMember) => {
    await setTeamMemberActive(m.id, !m.active)
    await auditService.log(user, 'team_member_deactivated', { targetName: m.name, details: { active: !m.active } })
    load()
  }

  if (loading) return <div className="ca-loading">Loading team members…</div>
  if (err) return <div className="ca-error">⚠ {err}</div>

  return (
    <div>
      <Toast msg={toast} />
      <div className="ca-toolbar">
        <button className="ca-btn primary" onClick={() => setEditing({ role: 'spotter', commission_basis: 'percent', commission_rate: 0.25, commission_amount: 0, risk_rating_days: 7, active: true })}>+ Add Team Member</button>
      </div>
      <div className="ca-card">
        <table className="ca-table">
          <thead><tr><th>Name</th><th>Role</th><th className="num">Commission</th><th className="num">Risk Rating</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={6} className="ca-none">No team members yet.</td></tr>
              : rows.map((m) => (
                <tr key={m.id}>
                  <td><b>{m.name}</b>{m.email ? <div className="ca-muted">{m.email}</div> : null}</td>
                  <td><span className={`chip ${m.role === 'buyer_sourcer' ? 'buyer' : 'spotter'}`}>{roleName(m.role)}</span></td>
                  <td className="num">{terms(m)}</td>
                  <td className="num">{m.risk_rating_days} days</td>
                  <td>{m.active ? <span className="chip ok">✓ Active</span> : <span className="chip muted">Inactive</span>}</td>
                  <td>
                    <div className="ca-actions">
                      <button className="ca-icon" title="Edit" onClick={() => setEditing(m)}>✎</button>
                      <button className="ca-icon" title={m.active ? 'Deactivate' : 'Reactivate'} onClick={() => toggleActive(m)}>{m.active ? '⦸' : '↺'}</button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <Modal title={editing.id ? 'Edit Team Member' : 'Add Team Member'} onClose={() => setEditing(null)}>
          <MemberForm value={editing} onCancel={() => setEditing(null)} onSave={save} />
        </Modal>
      )}
    </div>
  )
}

function MemberForm({ value, onSave, onCancel }: { value: Partial<TeamMember>; onSave: (m: Partial<TeamMember>) => Promise<void>; onCancel: () => void }) {
  const [m, setM] = useState<Partial<TeamMember>>(value)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const submit = async () => {
    if (!m.name || !m.role) { setError('Name and role are required.'); return }
    setBusy(true); setError(null)
    try { await onSave(m); toast.saved() } catch (e) { setError(e instanceof Error ? e.message : 'Save failed') } finally { setBusy(false) }
  }
  return (
    <div className="ca-form">
      <Field label="Full Name"><input value={m.name || ''} onChange={(e) => setM({ ...m, name: e.target.value })} placeholder="e.g. Sarah Chen" /></Field>
      <Field label="Email (optional)"><input value={m.email || ''} onChange={(e) => setM({ ...m, email: e.target.value })} placeholder="name@directauto.net.au" /></Field>
      <div className="ca-form-grid">
        <Field label="Role">
          <select value={m.role} onChange={(e) => setM({ ...m, role: e.target.value as any, commission_rate: (m.commission_basis === 'fixed') ? (m.commission_rate ?? 0) : (e.target.value === 'buyer_sourcer' ? 0.5 : 0.25) })}>
            <option value="spotter">Spotter</option>
            <option value="buyer_sourcer">Buyer-Sourcer</option>
            <option value="sales">Sales</option>
          </select>
        </Field>
        <Field label="Commission Basis">
          <select
            value={m.commission_basis || 'percent'}
            onChange={(e) => setM({ ...m, commission_basis: e.target.value as any })}
          >
            <option value="percent">Percentage of net margin</option>
            <option value="fixed">Fixed amount per deal</option>
          </select>
        </Field>
      </div>
      <div className="ca-form-grid">
        {(m.commission_basis || 'percent') === 'percent' ? (
          <Field label="Commission Rate (%)">
            <input type="number" min={0} max={100} step={0.01} value={asPercent(m.commission_rate ?? 0)} onChange={(e) => setM({ ...m, commission_rate: (parseFloat(e.target.value) || 0) / 100 })} />
          </Field>
        ) : (
          <Field label="Fixed Amount ($ per deal)">
            <input type="number" min={0} step={0.01} value={m.commission_amount ?? 0} onChange={(e) => setM({ ...m, commission_amount: parseFloat(e.target.value) || 0 })} />
          </Field>
        )}
        <div className="ca-note" style={{ alignSelf: 'end' }}>
          {(m.commission_basis || 'percent') === 'percent'
            ? 'A share of the net margin on each deal credited to this person — a loss is shared at the same rate.'
            : 'A flat fee on each deal credited to this person. It does not move with the margin, including on a deal that loses money.'}
        </div>
      </div>
      <Field label="Risk Rating (AR terms, days)">
        <select value={m.risk_rating_days ?? 7} onChange={(e) => setM({ ...m, risk_rating_days: parseInt(e.target.value, 10) })}>
          <option value={7}>7 days (High Risk)</option>
          <option value={14}>14 days (Medium Risk)</option>
          <option value={21}>21 days (Low Risk)</option>
        </select>
      </Field>
      {error && <div className="ca-form-error">{error}</div>}
      <div className="ca-form-actions">
        <button className="ca-btn" onClick={onCancel}>Cancel</button>
        <button className="ca-btn primary" disabled={busy} onClick={submit}>{busy ? 'Saving…' : 'Save Member'}</button>
      </div>
    </div>
  )
}

// ==========================================================================
// DEAL ATTRIBUTIONS
// ==========================================================================

function AttributionsTab({ user }: { user: User }) {
  const [rows, setRows] = useState<AttributionRow[]>([])
  const [deals, setDeals] = useState<DealBrief[]>([])
  const [members, setMembers] = useState<TeamMember[]>([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<AttributionRow | null>(null)

  const load = () => {
    setLoading(true)
    Promise.all([listAttributions(), listDealsBrief(), listTeamMembers()])
      .then(([a, d, m]) => { setRows(a); setDeals(d); setMembers(m) })
      .catch((e) => setErr(e.message)).finally(() => setLoading(false))
  }
  useEffect(load, [])

  const add = async (a: AttributionInput) => {
    await saveAttribution(a)
    const mem = members.find((m) => m.id === a.team_member_id)
    await auditService.log(user, 'deal_attributed', { targetName: mem?.name, details: { deal_id: a.deal_id, role: a.role, basis: a.basis, rate: a.rate, fixed_amount: a.fixed_amount } })
    setAdding(false); setToast('Attribution saved'); setTimeout(() => setToast(null), 2500); load()
  }
  const edit = async (a: AttributionInput) => {
    if (!editing) return
    await updateAttribution(editing.id, a)
    const mem = members.find((m) => m.id === a.team_member_id)
    await auditService.log(user, 'attribution_edited', {
      targetName: mem?.name,
      details: {
        attribution: editing.id,
        before: { deal: editing.dealId, member: editing.memberId, role: editing.role, rate: editing.rate },
        after: { deal: a.deal_id, member: a.team_member_id, role: a.role, basis: a.basis, rate: a.rate, fixed_amount: a.fixed_amount },
      },
    })
    setEditing(null); setToast('Attribution updated'); setTimeout(() => setToast(null), 2500); load()
  }

  const remove = async (r: AttributionRow) => {
    await deleteAttribution(r.id)
    await auditService.log(user, 'attribution_removed', { targetName: r.memberName, details: { deal: r.dealLabel } })
    load()
  }

  if (loading) return <div className="ca-loading">Loading attributions…</div>
  if (err) return <div className="ca-error">⚠ {err}</div>

  return (
    <div>
      <Toast msg={toast} />
      <div className="ca-toolbar">
        <button className="ca-btn primary" onClick={() => setAdding(true)}>+ Add Attribution</button>
      </div>
      <div className="ca-card">
        <table className="ca-table">
          <thead><tr><th>Vehicle</th><th>Team Member</th><th>Role</th><th className="num">Snapshotted Rate</th><th>Attributed</th><th>Actions</th></tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={6} className="ca-none">No attributions yet.</td></tr>
              : rows.map((r) => (
                <tr key={r.id}>
                  <td><b>{r.dealLabel}</b></td>
                  <td>{r.memberName}</td>
                  <td><span className={`chip ${r.role === 'buyer_sourcer' ? 'buyer' : 'spotter'}`}>{roleName(r.role)}</span></td>
                  <td className="num">{r.termsLabel}</td>
                  <td className="mono">{isoDate(r.createdAt)}</td>
                  <td>
                    <div className="ca-actions">
                      <button className="ca-icon" title="Edit attribution" onClick={() => setEditing(r)}>✎</button>
                      <button className="ca-icon" title="Remove" onClick={() => remove(r)}>🗑</button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <div className="ca-note">Commission terms are snapshotted from the member at the moment of attribution — the basis as well as the number, so a deal credited under a flat fee stays on that fee if the member later moves to a percentage. A deal can be credited to multiple members (e.g. a spotter and a buyer-sourcer); each is calculated separately, as <b>rate × gross profit</b> on the percentage basis or as the <b>fixed amount</b> on the fixed one.</div>

      {adding && (
        <Modal title="Add Deal Attribution" onClose={() => setAdding(false)}>
          <AttributionForm deals={deals} members={members} onCancel={() => setAdding(false)} onSave={add} />
        </Modal>
      )}

      {editing && (
        <Modal title="Edit Deal Attribution" onClose={() => setEditing(null)}>
          <AttributionForm
            deals={deals}
            members={members}
            initial={{ dealId: editing.dealId, memberId: editing.memberId }}
            saveLabel="Save changes"
            onCancel={() => setEditing(null)}
            onSave={edit}
          />
        </Modal>
      )}
    </div>
  )
}

function AttributionForm({ deals, members, onSave, onCancel, initial, saveLabel }: {
  deals: DealBrief[]
  members: TeamMember[]
  onSave: (a: any) => Promise<void>
  onCancel: () => void
  initial?: { dealId: string; memberId: string }
  saveLabel?: string
}) {
  const [dealId, setDealId] = useState(initial?.dealId || '')
  const [memberId, setMemberId] = useState(initial?.memberId || '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const member = members.find((m) => m.id === memberId)
  const submit = async () => {
    if (!dealId || !memberId || !member) { setError('Pick a deal and a team member.'); return }
    setBusy(true); setError(null)
    try {
      await onSave({
        deal_id: dealId,
        team_member_id: memberId,
        role: member.role,
        basis: member.commission_basis || 'percent',
        rate: member.commission_rate,
        fixed_amount: member.commission_amount || 0,
      })
      toast.saved()
    }
    catch (e) { setError(e instanceof Error ? e.message : 'Save failed') } finally { setBusy(false) }
  }
  return (
    <div className="ca-form">
      <Field label="Deal">
        <select value={dealId} onChange={(e) => setDealId(e.target.value)}>
          <option value="">Select a deal…</option>
          {deals.map((d) => <option key={d.id} value={d.id}>{d.label}{d.vin ? ` · ${d.vin}` : ''}</option>)}
        </select>
      </Field>
      <Field label="Team Member">
        <select value={memberId} onChange={(e) => setMemberId(e.target.value)}>
          <option value="">Select a team member…</option>
          {members
            .filter((m) => m.active || m.id === initial?.memberId)
            .map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}{m.active ? '' : ' (inactive)'} ({roleName(m.role)} {terms(m)})
              </option>
            ))}
        </select>
      </Field>
      <div className="ca-summary">
        <div><span className="ca-slabel">Role on deal</span><span className="ca-sval">{member ? roleName(member.role) : '—'}</span></div>
        <div><span className="ca-slabel">Snapshotted terms</span><span className="ca-sval">{member ? terms(member) : '—'}</span></div>
      </div>
      {error && <div className="ca-form-error">{error}</div>}
      <div className="ca-form-actions">
        <button className="ca-btn" onClick={onCancel}>Cancel</button>
        <button className="ca-btn primary" disabled={busy} onClick={submit}>{busy ? 'Saving…' : (saveLabel || 'Save Attribution')}</button>
      </div>
    </div>
  )
}

// ==========================================================================
// COMMISSION INVOICES
// ==========================================================================

function InvoicesTab({ user }: { user: User }) {
  const [rows, setRows] = useState<CommInvoiceRow[]>([])
  const [members, setMembers] = useState<TeamMember[]>([])
  const [deals, setDeals] = useState<DealBrief[]>([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  const load = () => {
    setLoading(true)
    Promise.all([listCommissionInvoicesAdmin(), listTeamMembers(), listDealsBrief()])
      .then(([i, m, d]) => { setRows(i); setMembers(m); setDeals(d) })
      .catch((e) => setErr(e.message)).finally(() => setLoading(false))
  }
  useEffect(load, [])

  const add = async (inv: any) => {
    const { duplicate } = await saveCommissionInvoice(inv)
    const mem = members.find((m) => m.id === inv.team_member_id)
    await auditService.log(user, duplicate ? 'commission_invoice_duplicate_blocked' : 'commission_invoice_logged', { targetName: mem?.name, details: { ref: inv.invoice_ref, amount: inv.amount, duplicate } })
    setAdding(false); setToast(duplicate ? '⚠ Duplicate detected — invoice logged & blocked' : 'Invoice logged'); setTimeout(() => setToast(null), 3500); load()
  }
  const remove = async (r: CommInvoiceRow) => {
    await deleteCommissionInvoice(r.id)
    await auditService.log(user, 'commission_invoice_deleted', { targetName: r.memberName, details: { ref: r.invoiceRef } })
    load()
  }

  /* Signed at the moment of the click: the bucket is private, and a signed URL sitting in a
     table of payables is a key that has been handed out. */
  const openDocument = async (r: CommInvoiceRow) => {
    try {
      const url = await commissionInvoiceFileUrl(r.filePath)
      if (url) window.open(url, '_blank', 'noopener')
      else setToast('That invoice has no document attached.')
    } catch (e) {
      setToast(e instanceof Error ? e.message : 'Could not open the invoice.')
    }
    setTimeout(() => setToast(null), 3500)
  }

  const statusChip = (s: string) =>
    s === 'blocked' ? <span className="chip bad">✗ Duplicate — Blocked</span>
      : s === 'matched' ? <span className="chip ok">✓ Matched</span>
      : <span className="chip info">Received</span>

  if (loading) return <div className="ca-loading">Loading invoices…</div>
  if (err) return <div className="ca-error">⚠ {err}</div>

  return (
    <div>
      <Toast msg={toast} />
      <div className="ca-toolbar">
        <button className="ca-btn primary" onClick={() => setAdding(true)}>+ Log Invoice</button>
      </div>
      <div className="ca-card">
        <table className="ca-table">
          <thead><tr><th>Invoice Ref</th><th>Payee</th><th>Deal</th><th className="num">Amount</th><th>Received</th><th>Status</th><th>Document</th><th>Actions</th></tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={8} className="ca-none">No commission invoices logged yet.</td></tr>
              : rows.map((r) => (
                <tr key={r.id}>
                  <td><b>{r.invoiceRef}</b>{r.notes ? <div className="ca-muted">{r.notes}</div> : null}</td>
                  <td>{r.memberName}</td>
                  <td>{r.dealLabel}</td>
                  <td className="num">{money(r.amount)}</td>
                  <td className="mono">{isoDate(r.receivedAt)}</td>
                  <td>{statusChip(r.status)}</td>
                  <td>
                    {r.filePath ? (
                      <button className="ca-linkbtn" onClick={() => void openDocument(r)} title={r.fileName || 'Open the invoice'}>
                        View invoice
                      </button>
                    ) : (
                      <span className="ca-muted">—</span>
                    )}
                  </td>
                  <td><div className="ca-actions"><button className="ca-icon" title="Delete" onClick={() => remove(r)}>🗑</button></div></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <div className="ca-note"><b>Byte-match fingerprinting:</b> on save, a SHA-256 fingerprint of payee + amount + content is created. An identical invoice matches an existing fingerprint and is flagged <b>Duplicate — Blocked</b> so it can never be released for payment.</div>

      {adding && (
        <Modal title="Log Commission Invoice" onClose={() => setAdding(false)}>
          <InvoiceForm members={members} deals={deals} onCancel={() => setAdding(false)} onSave={add} />
        </Modal>
      )}
    </div>
  )
}

function InvoiceForm({ members, deals, onSave, onCancel }: { members: TeamMember[]; deals: DealBrief[]; onSave: (i: any) => Promise<void>; onCancel: () => void }) {
  const [f, setF] = useState({ invoice_ref: '', team_member_id: '', deal_id: '', amount: '', received_at: todayISO(), content: '' })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /* The document itself. Winnie, 13 Sep: this log should take a file by drag and drop or by
     manual selection. Held until the form is submitted so an abandoned form leaves nothing
     behind in the bucket. */
  const [file, setFile] = useState<File | null>(null)
  const [reading, setReading] = useState(false)
  const [readNote, setReadNote] = useState<string | null>(null)

  /* 5 Nov 2026: the invoice was attached but every box was still typed by hand. Read it
     and fill the boxes that are still empty; a value already typed is never replaced, and
     the note says which boxes were filled so nothing changes silently. */
  const pickFile = async (picked: File | null) => {
    setFile(picked)
    setReadNote(null)
    if (!picked) return
    setReading(true)
    try {
      const text = await extractText(picked)
      if (!text.trim()) { setReadNote('The invoice could not be read. Enter the details by hand.'); return }
      const p = parseInvoiceText(text)
      const filled: string[] = []
      setF((prev) => {
        const next = { ...prev }
        if (!prev.invoice_ref.trim() && p.invoiceRef) { next.invoice_ref = p.invoiceRef; filled.push('invoice reference') }
        if (!prev.amount.trim() && p.totalCents !== null) { next.amount = (p.totalCents / 100).toFixed(2); filled.push('amount') }
        if (!prev.content.trim()) { next.content = text.slice(0, 4000); filled.push('invoice content') }
        if (!prev.team_member_id && p.supplier) {
          const who = p.supplier.toLowerCase()
          const hit = members.filter((m) => m.active && m.name && (who.includes(m.name.toLowerCase()) || m.name.toLowerCase().includes(who)))
          if (hit.length === 1) { next.team_member_id = hit[0].id; filled.push('payee') }
        }
        return next
      })
      setReadNote(filled.length ? 'Filled from the invoice: ' + filled.join(', ') + '. Check them before logging.' : 'The invoice was read, but every box it could fill already had a value.')
    } catch (e) {
      setReadNote('The invoice could not be read. Enter the details by hand.')
    } finally {
      setReading(false)
    }
  }

  const submit = async () => {
    if (!f.invoice_ref || !f.team_member_id || !f.amount) { setError('Invoice ref, payee, and amount are required.'); return }
    setBusy(true); setError(null)
    try {
      let doc: { path: string; name: string; type: string } | null = null
      if (file) doc = await uploadCommissionInvoiceFile(file)
      await onSave({
        ...f,
        amount: parseFloat(f.amount),
        deal_id: f.deal_id || null,
        file_path: doc ? doc.path : null,
        file_name: doc ? doc.name : null,
        file_type: doc ? doc.type : null,
      })
      toast.saved()
    }
    catch (e) { setError(e instanceof Error ? e.message : 'Save failed') } finally { setBusy(false) }
  }
  return (
    <div className="ca-form">
      <Field label="Invoice Reference"><input value={f.invoice_ref} onChange={(e) => setF({ ...f, invoice_ref: e.target.value })} placeholder="e.g. SC-INV-08241" /></Field>
      <div className="ca-form-grid">
        <Field label="Payee">
          <select value={f.team_member_id} onChange={(e) => setF({ ...f, team_member_id: e.target.value })}>
            <option value="">Select payee…</option>
            {members.filter((m) => m.active).map((m) => <option key={m.id} value={m.id}>{m.name} ({roleName(m.role)})</option>)}
          </select>
        </Field>
        <Field label="Deal (optional)">
          <select value={f.deal_id} onChange={(e) => setF({ ...f, deal_id: e.target.value })}>
            <option value="">— none —</option>
            {deals.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
          </select>
        </Field>
      </div>
      <div className="ca-form-grid">
        <Field label="Amount ($)"><input type="number" min={0} step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} placeholder="e.g. 1250" /></Field>
        <Field label="Date Received"><input type="date" value={f.received_at} onChange={(e) => setF({ ...f, received_at: e.target.value })} /></Field>
      </div>
      <Field label="Invoice Content / Notes"><textarea rows={3} value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} placeholder="Paste the invoice text or describe it (used for duplicate detection)" /></Field>
      {/* The same drop zone as every other upload surface on the platform, per the
          11 Sep global note: drag and drop everywhere, with manual selection kept. */}
      <Field label="Invoice Document (optional)">
        <FileDropZone
          accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
          label="Select invoice"
          hint={file ? file.name : 'PDF or image, up to 25 MB. The reference, amount and invoice text are filled in from it.'}
          busy={reading}
          busyLabel="Reading the invoice…"
          onFiles={(files) => pickFile(files[0] || null)}
        />
        {readNote && <small className="field-hint">{readNote}</small>}
      </Field>
      {error && <div className="ca-form-error">{error}</div>}
      <div className="ca-form-actions">
        <button className="ca-btn" onClick={onCancel}>Cancel</button>
        <button className="ca-btn primary" disabled={busy || reading} onClick={submit}>{busy ? 'Logging…' : 'Log Invoice'}</button>
      </div>
    </div>
  )
}

// ==========================================================================
// CAPITALISED COSTS
// ==========================================================================

function CostsTab({ user }: { user: User }) {
  const [rows, setRows] = useState<CostRow[]>([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState<CostRow | null>(null)

  const load = () => {
    setLoading(true)
    listDealsForCosts().then(setRows).catch((e) => setErr(e.message)).finally(() => setLoading(false))
  }
  useEffect(load, [])

  const save = async (id: string, transport: number, repairs: number, label: string) => {
    await saveCapitalisedCosts(id, transport, repairs)
    await auditService.log(user, 'capitalised_costs_updated', { targetName: label, details: { transport, repairs } })
    setEditing(null); setToast('Capitalised costs saved'); setTimeout(() => setToast(null), 2500); load()
  }

  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase()
    return t ? rows.filter((r) => r.label.toLowerCase().includes(t) || r.vin.toLowerCase().includes(t)) : rows
  }, [rows, search])

  if (loading) return <div className="ca-loading">Loading deals…</div>
  if (err) return <div className="ca-error">⚠ {err}</div>

  return (
    <div>
      <Toast msg={toast} />
      <div className="ca-toolbar">
        <input className="ca-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by vehicle or VIN…" />
      </div>
      <div className="ca-card">
        <table className="ca-table">
          <thead><tr><th>Vehicle</th><th>VIN</th><th className="num">Buy-In (incl GST)</th><th className="num">Transport</th><th className="num">Repairs</th><th className="num">Total Cap.</th><th className="num">Landed Cost</th><th>Actions</th></tr></thead>
          <tbody>
            {filtered.length === 0 ? <tr><td colSpan={8} className="ca-none">No deals match.</td></tr>
              : filtered.map((r) => (
                <tr key={r.id}>
                  <td><b>{r.label}</b></td>
                  <td className="mono">{r.vin || '—'}</td>
                  <td className="num">{money(r.buyIn)}</td>
                  <td className="num">{money(r.transport)}</td>
                  <td className="num">{money(r.repairs)}</td>
                  <td className="num">{money(r.totalCapitalised)}</td>
                  <td className="num">{money(r.landedCost)}</td>
                  <td><div className="ca-actions"><button className="ca-icon" title="Edit costs" onClick={() => setEditing(r)}>✎</button></div></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <div className="ca-note"><b>Capitalised:</b> freight/transport to lot, PPSR lodgement, inspection, reconditioning. <b>NOT capitalised:</b> commission, spotter/buyer fees, load fees, advertising. Landed cost = buy-in + capitalised − 1/11th notional GST; used for month-end stocktake &amp; the closing-stock journal.</div>

      {editing && (
        <Modal title="Edit Capitalised Costs" onClose={() => setEditing(null)}>
          <CostForm row={editing} onCancel={() => setEditing(null)} onSave={save} />
        </Modal>
      )}
    </div>
  )
}

function CostForm({ row, onSave, onCancel }: { row: CostRow; onSave: (id: string, t: number, r: number, label: string) => Promise<void>; onCancel: () => void }) {
  const [transport, setTransport] = useState(String(row.transport))
  const [repairs, setRepairs] = useState(String(row.repairs))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const t = parseFloat(transport) || 0
  const r = parseFloat(repairs) || 0
  const totalCap = t + r
  const landed = row.buyIn + totalCap - row.buyIn / 11
  const submit = async () => {
    setBusy(true); setError(null)
    try { await onSave(row.id, t, r, row.label); toast.saved() } catch (e) { setError(e instanceof Error ? e.message : 'Save failed') } finally { setBusy(false) }
  }
  return (
    <div className="ca-form">
      <div className="ca-readonly">
        <div><span className="ca-slabel">Vehicle</span><span className="ca-sval">{row.label}</span></div>
        <div><span className="ca-slabel">Buy-In (incl GST)</span><span className="ca-sval">{money(row.buyIn)}</span></div>
      </div>
      <div className="ca-form-grid">
        <Field label="Transport / Freight ($)"><input type="number" min={0} step="0.01" value={transport} onChange={(e) => setTransport(e.target.value)} /></Field>
        <Field label="Repairs / Reconditioning ($)"><input type="number" min={0} step="0.01" value={repairs} onChange={(e) => setRepairs(e.target.value)} /></Field>
      </div>
      <div className="ca-summary">
        <div><span className="ca-slabel">Total Capitalised</span><span className="ca-sval">{money(totalCap, 2)}</span></div>
        <div><span className="ca-slabel">Landed Cost (Buy-In + Cap − ITC)</span><span className="ca-sval">{money(landed, 2)}</span></div>
      </div>
      {error && <div className="ca-form-error">{error}</div>}
      <div className="ca-form-actions">
        <button className="ca-btn" onClick={onCancel}>Cancel</button>
        <button className="ca-btn primary" disabled={busy} onClick={submit}>{busy ? 'Saving…' : 'Save Costs'}</button>
      </div>
    </div>
  )
}

// ==========================================================================
// shared bits
// ==========================================================================

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="ca-field"><span>{label}</span>{children}</label>
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  return (
    <div className="ca-modal" onClick={onClose}>
      <div className="ca-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="ca-modal-h"><h3>{title}</h3><button className="ca-close" onClick={onClose}>✕</button></div>
        <div className="ca-modal-b">{children}</div>
      </div>
    </div>
  )
}
